/**
 * CREATIVE DETONATOR - Copy Prompt Dropdown
 * Universal prompt copy with format options for all idea types
 *
 * Features:
 * - Format-specific configurations (print, social, video, etc.)
 * - Orientation and aspect ratio selection
 * - Streamlined prompts for AI image generators
 * - Complete Prompt option for full context (AI chat)
 * - Video format includes scene breakdown
 * v65 - Fixed close behavior, context passing, added Complete Prompt
 *      - Restored full format context in prompts (format, orientation, aspect)
 * v66 - Fixed dropdown being destroyed when canvas re-renders
 *      - Dropdown now stays in document.body, positioned via CSS coordinates
 */

// v64 - Self-rendering module with fixed close behavior
import { GeneratorState } from '../core/state.js';
import { injectModalHtml } from '../../../js/shared/dom-utils.js';

// ============================================================================
// HTML TEMPLATE - Self-rendering dropdown (window-titlebar version)
// ============================================================================

const DROPDOWN_HTML = `
<div class="copy-prompt-dropdown" id="copy-prompt-dropdown">
  <div class="dropdown-modal">
    <div class="window-titlebar dropdown-titlebar">
      <div class="window-close modal-close-btn" id="dropdown-close" aria-label="Close"></div>
      <div class="window-title">◆ COPY PROMPT</div>
    </div>
    <div class="dropdown-body">
      <div class="dropdown-options">
        <div class="dropdown-option-group">
          <label class="dropdown-label">FORMAT</label>
          <select id="prompt-orientation" class="dropdown-select"></select>
        </div>
        <div class="dropdown-option-group" id="aspect-group">
          <label class="dropdown-label">ASPECT</label>
          <select id="prompt-aspect" class="dropdown-select"></select>
        </div>
      </div>
      <div class="dropdown-prompt-section">
        <textarea id="dropdown-prompt-text" class="dropdown-textarea" spellcheck="false"></textarea>
      </div>
      <div class="dropdown-tip">
        <span class="tip-icon-blue">i</span>
        <span class="tip-text-blue">Paste into Midjourney, DALL-E, Ideogram, or ChatGPT</span>
      </div>
    </div>
    <div class="dropdown-footer">
      <button class="btn-copy-full" id="btn-copy-full">COPY TO CLIPBOARD</button>
    </div>
  </div>
</div>
`;

// ============================================================================
// FORMAT CONFIGURATIONS
// ============================================================================

/**
 * Format configurations for each idea type
 * isImageFormat: true = optimized for AI image generators (Midjourney, DALL-E, etc.)
 *                false = includes full context (benefit, concept, idea)
 */
const FORMAT_CONFIGS = {
  print: {
    prefix: "Create a print advertisement.",
    isImageFormat: true,
    orientations: [
      { value: 'complete', label: '◆ Complete Prompt (AI Chat)' },
      { value: 'horizontal', label: 'Horizontal Billboard' },
      { value: 'vertical', label: 'Vertical Poster' },
      { value: 'square', label: 'Square Format' }
    ],
    aspectRatios: {
      complete: [],
      horizontal: ['16:9', '4:3', '3:1', '2:1'],
      vertical: ['9:16', '2:3', '1:2'],
      square: ['1:1']
    },
    sectionLabel: 'VISUAL'
  },
  social: {
    prefix: "Create a social media visual.",
    isImageFormat: true,
    orientations: [
      { value: 'complete', label: '◆ Complete Prompt (AI Chat)' },
      { value: 'portrait', label: 'Reel/Story (9:16)' },
      { value: 'square', label: 'Feed Post (1:1)' },
      { value: 'landscape', label: 'YouTube (16:9)' }
    ],
    aspectRatios: {
      complete: [],
      portrait: ['9:16'],
      square: ['1:1'],
      landscape: ['16:9']
    },
    sectionLabel: 'VISUAL'
  },
  video: {
    prefix: "Create a cinematic video frame.",
    isImageFormat: true,
    orientations: [
      { value: 'complete', label: '◆ Complete Prompt (AI Chat)' },
      { value: 'single-frame', label: 'Single Frame (Hero)' },
      { value: 'storyboard-6', label: 'Storyboard (6 panels)' },
      { value: 'storyboard-9', label: 'Storyboard (9 panels)' },
      { value: 'storyboard-12', label: 'Storyboard (12 panels)' }
    ],
    aspectRatios: {
      complete: [],
      'single-frame': ['16:9', '2.35:1', '9:16', '1:1'],
      'storyboard-6': ['3:2'],
      'storyboard-9': ['1:1'],
      'storyboard-12': ['4:3']
    },
    sectionLabel: 'VISUAL',
    // Panel configs for storyboard options
    panelConfigs: {
      'storyboard-6': { panelCount: 6, rows: 2, cols: 3 },
      'storyboard-9': { panelCount: 9, rows: 3, cols: 3 },
      'storyboard-12': { panelCount: 12, rows: 3, cols: 4 }
    }
  },
  experiential: {
    prefix: "Create a brand activation visualization.",
    isImageFormat: true,
    orientations: [
      { value: 'complete', label: '◆ Complete Prompt (AI Chat)' },
      { value: 'wide', label: 'Wide Environment' },
      { value: 'hero', label: 'Hero Installation' },
      { value: 'detail', label: 'Interaction Detail' }
    ],
    aspectRatios: {
      complete: [],
      wide: ['21:9', '16:9'],
      hero: ['4:3', '3:2'],
      detail: ['1:1', '4:5']
    },
    sectionLabel: 'VISUAL'
  },
  digital: {
    prefix: "Create a digital banner advertisement.",
    isImageFormat: true,
    orientations: [
      { value: 'complete', label: '◆ Complete Prompt (AI Chat)' },
      { value: 'leaderboard', label: 'Leaderboard (728x90)' },
      { value: 'mrec', label: 'Medium Rect (300x250)' },
      { value: 'skyscraper', label: 'Skyscraper (160x600)' }
    ],
    aspectRatios: {
      complete: [],
      leaderboard: ['8:1'],
      mrec: ['6:5'],
      skyscraper: ['4:15']
    },
    sectionLabel: 'VISUAL'
  },
  campaign: {
    prefix: "Create a campaign key visual.",
    isImageFormat: true,
    orientations: [
      { value: 'complete', label: '◆ Complete Prompt (AI Chat)' },
      { value: 'landscape', label: 'Landscape Hero' },
      { value: 'portrait', label: 'Portrait Key Art' },
      { value: 'square', label: 'Square Key Visual' }
    ],
    aspectRatios: {
      complete: [],
      landscape: ['16:9', '2:1'],
      portrait: ['2:3', '9:16'],
      square: ['1:1']
    },
    sectionLabel: 'VISUAL'
  }
};

// ============================================================================
// STATE
// ============================================================================

// Current dropdown state
let currentDropdownIdea = null;
let currentDropdownBrief = null;
let globalListenersRegistered = false;

// ============================================================================
// INITIALIZATION
// ============================================================================

/**
 * Setup event listeners on dropdown element
 * Extracted so it can be called on re-injection
 * @param {HTMLElement} dropdown - The dropdown element
 */
function setupDropdownListeners(dropdown) {
  if (!dropdown) return;

  const closeBtn = dropdown.querySelector('#dropdown-close');
  const orientationSelect = dropdown.querySelector('#prompt-orientation');
  const aspectSelect = dropdown.querySelector('#prompt-aspect');
  const copyFullBtn = dropdown.querySelector('#btn-copy-full');

  // Close button handler
  closeBtn?.addEventListener('click', (e) => {
    e.stopPropagation();
    closeCopyDropdown();
  });

  // Stop propagation on all clicks inside dropdown (prevent card selection)
  dropdown.addEventListener('click', (e) => {
    e.stopPropagation();
  });

  // Orientation change -> update aspect ratios
  orientationSelect?.addEventListener('change', () => {
    updateAspectRatios();
    updateDropdownPromptText();
  });

  // Aspect ratio change -> update prompt
  aspectSelect?.addEventListener('change', updateDropdownPromptText);

  // Copy button
  copyFullBtn?.addEventListener('click', () => {
    const textarea = document.getElementById('dropdown-prompt-text');
    if (textarea?.value) {
      navigator.clipboard.writeText(textarea.value).then(() => {
        copyFullBtn.textContent = 'COPIED!';
        copyFullBtn.classList.add('copied');
        setTimeout(() => {
          copyFullBtn.textContent = 'COPY TO CLIPBOARD';
          copyFullBtn.classList.remove('copied');
        }, 2000);
      });
    }
  });
}

/**
 * Initialize the copy prompt dropdown system
 */
export function initCopyPromptDropdown() {
  // Self-render: inject dropdown HTML if not already present
  injectModalHtml('copy-prompt-dropdown', DROPDOWN_HTML);

  const dropdown = document.getElementById('copy-prompt-dropdown');
  if (!dropdown) return;

  // Setup event listeners on dropdown
  setupDropdownListeners(dropdown);

  // Register global listeners ONCE (persistent)
  if (!globalListenersRegistered) {
    // Close on click outside (global, persistent)
    document.addEventListener('click', (e) => {
      const dropdown = document.getElementById('copy-prompt-dropdown');
      if (!dropdown?.classList.contains('active')) return;

      // Don't close if clicking inside dropdown or on copy button
      if (e.target.closest('.copy-prompt-dropdown') ||
          e.target.closest('.idea-copy-btn')) {
        return;
      }
      closeCopyDropdown();
    });

    // Close on Escape (global, persistent)
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        const dropdown = document.getElementById('copy-prompt-dropdown');
        if (dropdown?.classList.contains('active')) {
          closeCopyDropdown();
        }
      }
    });

    globalListenersRegistered = true;
  }

  // Dashboard ideas grid delegation
  const dashboardGrid = document.getElementById('ideas-grid');
  if (dashboardGrid) {
    dashboardGrid.addEventListener('click', (e) => {
      const btn = e.target.closest('.idea-copy-btn');
      if (btn) {
        e.stopPropagation();
        // Context should be set by ideas.js capture-phase listener
        openCopyDropdown(btn.dataset.id);
      }
    });
  }

  // Harvest results delegation
  const harvestResults = document.getElementById('harvest-results');
  if (harvestResults) {
    harvestResults.addEventListener('click', (e) => {
      const btn = e.target.closest('.idea-copy-btn');
      if (btn) {
        e.stopPropagation();
        openCopyDropdown(btn.dataset.id);
      }
    });
  }

  // NOTE: Canvas view is now handled directly by idea-canvas.js
  // which calls openCopyDropdown with idea and brief objects
}

// ============================================================================
// DROPDOWN OPEN/CLOSE
// ============================================================================

/**
 * Open the copy dropdown for an idea
 * @param {string} ideaId - The ID of the idea
 * @param {Object} ideaObject - Optional: idea object passed directly (avoids lookup)
 * @param {Object} briefObject - Optional: brief object (product, benefit)
 */
export function openCopyDropdown(ideaId, ideaObject = null, briefObject = null) {
  // Find the idea - use passed object or lookup from GeneratorState
  let targetIdea = ideaObject;

  if (!targetIdea) {
    for (const group of GeneratorState.generatedIdeas) {
      const found = group.ideas.find(idea => idea.id === ideaId);
      if (found) {
        targetIdea = found;
        break;
      }
    }
  }

  if (!targetIdea) {
    console.error('[CopyDropdown] Idea not found:', ideaId);
    return;
  }

  currentDropdownIdea = targetIdea;

  // Determine brief - use passed, then idea.meta.brief, then GeneratorState.brief
  currentDropdownBrief = briefObject ||
                         targetIdea.meta?.brief ||
                         GeneratorState.brief ||
                         { product: '', benefit: '' };

  let dropdown = document.getElementById('copy-prompt-dropdown');
  if (!dropdown) {
    // Re-inject dropdown if it was destroyed (e.g., canvas re-render)
    console.log('[CopyDropdown] Re-injecting dropdown');
    injectModalHtml('copy-prompt-dropdown', DROPDOWN_HTML);
    dropdown = document.getElementById('copy-prompt-dropdown');
    if (!dropdown) {
      console.error('[CopyDropdown] Failed to re-inject dropdown');
      return;
    }
    // Re-attach event listeners to new element
    setupDropdownListeners(dropdown);
  }

  const format = targetIdea.format || 'print';
  const config = FORMAT_CONFIGS[format] || FORMAT_CONFIGS.print;

  // Populate orientation select
  const orientationSelect = document.getElementById('prompt-orientation');
  const aspectSelect = document.getElementById('prompt-aspect');

  if (!orientationSelect || !aspectSelect) {
    console.error('[CopyDropdown] Select elements not found');
    return;
  }

  orientationSelect.innerHTML = config.orientations.map(opt =>
    `<option value="${opt.value}">${opt.label}</option>`
  ).join('');

  // Update aspect ratios for default orientation
  updateAspectRatios();

  // Generate initial prompt text
  updateDropdownPromptText();

  // Position dropdown near the button using CSS coordinates
  // IMPORTANT: Do NOT move dropdown into canvas DOM - it gets destroyed on re-render
  const canvasView = document.getElementById('canvas-view');
  const isCanvasOpen = canvasView?.classList.contains('active');

  let btn;
  if (isCanvasOpen) {
    btn = canvasView.querySelector(`.idea-copy-btn[data-id="${ideaId}"]`);
  } else {
    btn = document.querySelector(`.idea-copy-btn[data-id="${ideaId}"]`);
  }

  // Ensure dropdown is in document.body (not inside canvas)
  if (dropdown.parentElement !== document.body) {
    document.body.appendChild(dropdown);
  }

  // Position dropdown near button
  if (btn) {
    const btnRect = btn.getBoundingClientRect();
    const dropdownWidth = 340; // Approximate dropdown width

    // Position below the button, aligned to the right edge
    let left = btnRect.right - dropdownWidth;
    let top = btnRect.bottom + 8;

    // Keep within viewport
    if (left < 16) left = 16;
    if (left + dropdownWidth > window.innerWidth - 16) {
      left = window.innerWidth - dropdownWidth - 16;
    }

    // If dropdown would go below viewport, position above button
    const dropdownHeight = 400; // Approximate max height
    if (top + dropdownHeight > window.innerHeight - 16) {
      top = btnRect.top - dropdownHeight - 8;
      if (top < 16) top = 16;
    }

    dropdown.style.position = 'fixed';
    dropdown.style.left = `${left}px`;
    dropdown.style.top = `${top}px`;
    dropdown.style.zIndex = '10001'; // Above canvas overlay
  }

  // Show dropdown
  dropdown.classList.add('active');
}

/**
 * Close the copy dropdown
 */
export function closeCopyDropdown() {
  const dropdown = document.getElementById('copy-prompt-dropdown');
  dropdown?.classList.remove('active');
  currentDropdownIdea = null;
  currentDropdownBrief = null;
  // NOTE: Don't cleanup global listeners - they're persistent
}

// ============================================================================
// DROPDOWN UPDATES
// ============================================================================

/**
 * Update aspect ratio options based on selected orientation
 */
function updateAspectRatios() {
  if (!currentDropdownIdea) return;

  const format = currentDropdownIdea.format || 'print';
  const config = FORMAT_CONFIGS[format] || FORMAT_CONFIGS.print;
  const orientationSelect = document.getElementById('prompt-orientation');
  const aspectSelect = document.getElementById('prompt-aspect');
  const aspectGroup = document.getElementById('aspect-group');

  if (!orientationSelect || !aspectSelect) return;

  const orientation = orientationSelect.value;

  // Hide aspect ratio for "complete" prompt
  if (orientation === 'complete') {
    if (aspectGroup) aspectGroup.style.display = 'none';
    aspectSelect.innerHTML = '';
    return;
  } else {
    if (aspectGroup) aspectGroup.style.display = '';
  }

  const ratios = config.aspectRatios[orientation] || ['16:9'];
  aspectSelect.innerHTML = ratios.map(ratio =>
    `<option value="${ratio}">${ratio}</option>`
  ).join('');
}

/**
 * Update the prompt text based on current selections
 * For image formats: streamlined prompt optimized for AI image generators
 * For storyboard: uses wrapper approach (preserves original content)
 * For complete: full context with all fields
 * For other formats: includes full context (benefit, concept, idea)
 */
function updateDropdownPromptText() {
  if (!currentDropdownIdea) return;

  const idea = currentDropdownIdea;
  const format = (idea.format || 'print').toLowerCase();
  const config = FORMAT_CONFIGS[format] || FORMAT_CONFIGS.print;
  const brief = currentDropdownBrief || GeneratorState.brief || {};

  const orientationSelect = document.getElementById('prompt-orientation');
  const aspectSelect = document.getElementById('prompt-aspect');
  const textarea = document.getElementById('dropdown-prompt-text');

  const orientation = orientationSelect.value || 'landscape';
  const aspectRatio = aspectSelect.value || '16:9';

  let prompt = '';

  // Check if this is a storyboard option (for video format)
  const isStoryboard = orientation.startsWith('storyboard-');

  if (orientation === 'complete') {
    // COMPLETE PROMPT - Full context for AI chat
    prompt = buildCompletePrompt(idea, brief);
  } else if (isStoryboard && config.panelConfigs) {
    // STORYBOARD PROMPT - Uses wrapper approach
    const panelConfig = config.panelConfigs[orientation];
    prompt = buildStoryboardDropdownPrompt(idea, brief, panelConfig, aspectRatio);
  } else if (config.isImageFormat) {
    // PROMPT FOR AI IMAGE GENERATORS - includes format context
    const formatName = format === 'print' ? 'print advertisement' :
                       format === 'social' ? 'social media visual' :
                       format === 'video' ? 'video frame' :
                       format === 'experiential' ? 'brand activation visualization' :
                       format === 'digital' ? 'digital banner' :
                       format === 'campaign' ? 'campaign key visual' : 'advertisement';

    const orientationLabel = orientationSelect.options[orientationSelect.selectedIndex]?.text || '';

    prompt = `Create a ${formatName}.\n`;
    prompt += `Format: ${orientationLabel}, ${aspectRatio} aspect ratio.\n\n`;

    // Add main visual description
    if (idea.text) {
      prompt += idea.text;
    }

    // Add headline if present
    if (idea.headline && idea.headline !== '[Visual Only]') {
      prompt += `\n\nHeadline: "${idea.headline}"`;
      if (idea.subline) {
        prompt += `\nSubline: "${idea.subline}"`;
      }
    }

    // Add style guidance and aspect ratio
    prompt += `\n\nStyle: Professional advertising photography, high production value.`;
    prompt += `\n--ar ${aspectRatio}`;

  } else {
    // FULL CONTEXT PROMPT (for non-image formats)
    const orientationLabel = orientationSelect.options[orientationSelect.selectedIndex]?.text || 'Horizontal';

    prompt = `${config.prefix}\n\n`;
    prompt += `Format: ${orientationLabel}, ${aspectRatio} aspect ratio\n`;
    prompt += `Product: ${brief.product || 'Product'}\n`;
    if (brief.benefit) {
      prompt += `Benefit: ${brief.benefit}\n`;
    }
    prompt += `\n`;

    if (idea.title) {
      prompt += `${idea.title}\n\n`;
    }

    if (idea.idea) {
      prompt += `THE IDEA:\n${idea.idea}\n\n`;
    }

    const sectionLabel = idea.sectionLabel || config.sectionLabel || 'VISUAL';
    if (idea.text) {
      prompt += `${sectionLabel}:\n${idea.text}\n\n`;
    }

    if (idea.headline && idea.headline !== '[Visual Only]') {
      const headlineLabel = idea.headlineLabel || 'HEADLINE';
      prompt += `${headlineLabel}: "${idea.headline}"\n`;
    }

    if (idea.subline) {
      prompt += `SUBLINE: "${idea.subline}"\n`;
    }

    prompt += `\n`;
    prompt += `Style: Professional advertising photography, high production value, ${format} format optimized.\n`;
    prompt += `--ar ${aspectRatio}`;
  }

  textarea.value = prompt;
}

/**
 * Build COMPLETE prompt with full context for AI chat
 * Includes product, benefit, idea, headline, visual, why it works
 * For video format: also includes scene breakdown if available
 * @param {Object} idea - The idea object
 * @param {Object} brief - The brief object (product, benefit)
 * @returns {string} Complete prompt
 */
function buildCompletePrompt(idea, brief) {
  const format = (idea.format || 'print').toLowerCase();
  let prompt = `# ${format.toUpperCase()} AD CONCEPT\n\n`;

  prompt += `**Product:** ${brief.product || 'Product'}\n`;
  if (brief.benefit) {
    prompt += `**Benefit:** ${brief.benefit}\n`;
  }
  prompt += '\n';

  if (idea.title) {
    prompt += `## ${idea.title}\n\n`;
  }

  if (idea.idea) {
    prompt += `### THE IDEA\n${idea.idea}\n\n`;
  }

  if (idea.headline && idea.headline !== '[Visual Only]') {
    prompt += `### HEADLINE\n"${idea.headline}"\n`;
    if (idea.subline) {
      prompt += `Subline: ${idea.subline}\n`;
    }
    prompt += '\n';
  }

  if (idea.text) {
    const sectionLabel = idea.sectionLabel || 'VISUAL';
    prompt += `### ${sectionLabel}\n${idea.text}\n\n`;
  }

  if (idea.whyItWorks) {
    prompt += `### WHY IT WORKS\n${idea.whyItWorks}\n\n`;
  }

  // VIDEO FORMAT: Include scene breakdown if available
  if (format === 'video') {
    const scenes = idea.meta?.scenes || idea.meta?.generatedScenes || [];
    if (scenes.length > 0) {
      prompt += `### SCENE BREAKDOWN\n`;
      scenes.forEach((scene, i) => {
        prompt += `\n**Scene ${i + 1}** (${scene.timing || '?s'})\n`;
        prompt += `Visual: ${scene.visual || scene.description || ''}\n`;
        if (scene.camera) prompt += `Camera: ${scene.camera}\n`;
        if (scene.audio) prompt += `Audio: ${scene.audio}\n`;
      });
    }
  }

  return prompt.trim();
}

/**
 * Build storyboard prompt - clean, simple format
 * @param {Object} idea - The idea object
 * @param {Object} brief - The brief object (unused - keeping prompts clean)
 * @param {Object} panelConfig - Panel configuration {panelCount, rows, cols}
 * @param {string} aspectRatio - Grid aspect ratio
 * @returns {string} Storyboard prompt
 */
function buildStoryboardDropdownPrompt(idea, brief, panelConfig, aspectRatio) {
  const { panelCount, rows, cols } = panelConfig;
  const format = idea.format || 'video';

  let prompt = `Create a ${rows}x${cols} panel storyboard grid (${panelCount} panels) for this ${format} ad concept:\n\n`;

  // Just the visual/execution - no brief clutter
  if (idea.text) {
    prompt += idea.text;
  }

  // Add headline if present
  if (idea.headline && idea.headline !== '[Visual Only]') {
    prompt += `\n\nHeadline: "${idea.headline}"`;
  }

  // Simple requirements
  prompt += `\n\nRequirements:
- Single cohesive image with exactly ${panelCount} panels in a ${rows}x${cols} grid
- Each panel is a distinct scene showing story progression
- Consistent visual style across all panels
- ${aspectRatio} aspect ratio for the overall grid
- Clear panel borders for easy splitting
- Cinematic commercial quality`;

  return prompt;
}

// ============================================================================
// GLOBAL EXPORTS FOR COMPATIBILITY
// ============================================================================

// Expose to window for compatibility
window.initCopyPromptDropdown = initCopyPromptDropdown;
window.openCopyDropdown = openCopyDropdown;
window.closeCopyDropdown = closeCopyDropdown;
